import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { Toast as ToastComponent } from '../ui';

const TIMEOUT = 5000;
const mainPopupContainer = document.getElementById('popup');
const TOAST_PRIMARY_COLOR = 'rgb(var(--color-primary))';

interface PopUpProps {
  message: string;
  properties: propertyType;
  toastContainer?: HTMLElement | null | undefined;
}

interface propertyType {
  background: string;
  textColor: string;
  headerText: string;
  variant: string;
  toastWrapperClass?: string;
}
export const Popup = ({ message, properties, toastContainer = mainPopupContainer }: PopUpProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const removePopUp = () => {
    setIsOpen(false);
    // Wait for exit animation before hiding popover
    setTimeout(() => {
      try {
        popoverRef.current?.hidePopover();
      } catch {
        // Popover might already be hidden
      }
      ReactDOM.render(<></>, toastContainer);
    }, 300);
  };

  // Show popover in top layer and trigger entrance animation
  useEffect(() => {
    if (popoverRef.current) {
      try {
        popoverRef.current.showPopover();
        // Small delay to trigger CSS transition
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      } catch {
        // Popover API not supported, still show the element
        setIsVisible(true);
      }
    }
  }, []);

  // Re-show the popover after a short delay to ensure it stays on top of any
  // modals that might open right after the toast (e.g., Call Details modal)
  useEffect(() => {
    const reShowTimer = setTimeout(() => {
      if (popoverRef.current && isOpen) {
        try {
          // Hide and re-show to move to top of top-layer stack
          popoverRef.current.hidePopover();
          popoverRef.current.showPopover();
        } catch {
          // Popover API not supported
        }
      }
    }, 100);

    return () => clearTimeout(reShowTimer);
  }, [isOpen]);

  // Auto-hide after timeout
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      removePopUp();
    }, TIMEOUT);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fix the exit button to prevent form submission by setting type="button"
  useEffect(() => {
    if (!isOpen) return;

    // Use a small delay to ensure the ToastComponent is fully rendered
    const timeoutId = setTimeout(() => {
      if (toastRef.current) {
        const exitButton = toastRef.current.querySelector('button[aria-label="Close"]') as HTMLButtonElement;
        if (exitButton) {
          // Set type to button to prevent form submission (defaults to "submit" in forms)
          exitButton.type = 'button';
        }
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  return (
    <div
      ref={popoverRef}
      // @ts-expect-error - popover is a valid HTML attribute but not yet in React types
      popover="manual"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        maxWidth: '100vw',
        maxHeight: 'none',
        width: '100vw',
        height: 'auto',
        overflow: 'visible',
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
      className="toast-popover-toplayer"
    >
      <style>
        {`
          .toast-popover-toplayer::backdrop {
            display: none;
          }
          .toast-primary > div > div:first-of-type {
            color: ${TOAST_PRIMARY_COLOR} !important;
          }
          .toast-primary button [stroke],
          .toast-primary button [fill] {
            stroke: ${TOAST_PRIMARY_COLOR} !important;
            fill: ${TOAST_PRIMARY_COLOR} !important;
          }
          .toast-error > div > div:first-of-type {
            color: #991b1b !important;
          }
          .toast-error button [stroke],
          .toast-error button [fill] {
            stroke: #991b1b !important;
            fill: #991b1b !important;
          }
          .toast-warning > div > div:first-of-type {
            color: #1e40af !important;
          }
          .toast-warning button [stroke],
          .toast-warning button [fill] {
            stroke: #1e40af !important;
            fill: #1e40af !important;
          }
        `}
      </style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          paddingTop: window.innerWidth >= 768 ? '2rem' : 0,
          transform: isOpen && isVisible ? 'translateY(0)' : 'translateY(-100%)',
          opacity: isOpen && isVisible ? 1 : 0,
          transition: 'transform 300ms ease-out, opacity 300ms ease-out',
          pointerEvents: 'auto',
        }}
      >
        <div ref={toastRef} className={properties.toastWrapperClass}>
          <ToastComponent
            className="md:min-w-150 w-full md:w-auto md:rounded-lg md:p-5"
            style={{ backgroundColor: properties.background, color: properties.textColor }}
            onExit={removePopUp}
            title={
              properties?.headerText ? properties.headerText : properties.variant === 'success' ? 'Success!' : 'Error!'
            }
            description={message}
            variant={properties.variant as 'success' | 'error'}
          />
        </div>
      </div>
    </div>
  );
};

const ToastProperties = {
  success: {
    variant: 'success',
    background: '#f3f3fa',
    textColor: TOAST_PRIMARY_COLOR,
    headerText: 'Success!',
    toastWrapperClass: 'toast-primary',
  },
  error: {
    variant: 'error',
    background: '#fef2f2',
    textColor: '#991b1b',
    headerText: 'Oops!',
    toastWrapperClass: 'toast-error',
  },
  warning: {
    variant: 'error',
    background: '#eff6ff',
    textColor: '#1e40af',
    headerText: 'Oops!',
    toastWrapperClass: 'toast-warning',
  },
};

const generatePopUp = (
  message: any,
  properties: propertyType,
  toastContainer: HTMLElement | null | undefined = mainPopupContainer,
) => {
  ReactDOM.render(<Popup message={message} properties={properties} toastContainer={toastContainer} />, toastContainer);
};

const Toast = {
  success: (
    message: any,
    headerText = ToastProperties.success.headerText,
    toastContainer: HTMLElement | null | undefined = mainPopupContainer,
  ) =>
    generatePopUp(
      message,
      {
        ...ToastProperties.success,
        headerText,
      },
      toastContainer,
    ),
  error: (
    message: any,
    headerText = ToastProperties.error.headerText,
    toastContainer: HTMLElement | null | undefined = mainPopupContainer,
  ) =>
    generatePopUp(
      message,
      {
        ...ToastProperties.error,
        headerText,
      },
      toastContainer,
    ),
  warning: (message: any, toastContainer: HTMLElement | null | undefined = mainPopupContainer) =>
    generatePopUp(message, ToastProperties.warning, toastContainer),
};

export default Toast;
